import React from 'react';
import { SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { Field, Select } from '../../ui/Field';
import { Toggle } from '../../ui/ChoiceGroup';
import { useDiscover } from '../../../contexts/DiscoverContext';
import { TUITION_CEILING, type DeadlineWindow } from '../../../utils/discover';

/**
 * Design B's refinement rail.
 *
 * Every control writes to the same filter state Design A's chips write to, so a
 * refinement made here survives the composition changing. Each control states
 * what it actually does where that is not obvious.
 */
const DEADLINES: DeadlineWindow[] = ['Anytime', 'This month', 'Next 3 months', 'This year'];
export function RefinePanel() {
  const {
    filters,
    setFilter,
    clearFilter,
    clearFilters,
    activeChips,
    countries,
    locations,
    fields
  } = useDiscover();
  return <aside aria-label="Refine your search" className="rounded-card border border-line bg-surface p-5" data-id="element-1444" data-mp="2ako8ro3yd1gc">
      <div className="flex items-center justify-between gap-3" data-id="element-1445" data-mp="25jgztlq2pw83">
        <h2 className="text-body font-semibold text-ink" data-id="element-1446" data-mp="1jhbue4hc8wt1">Refine your search</h2>
        <button type="button" onClick={clearFilters} disabled={activeChips.length === 0} className="text-body-sm font-medium text-brand-700 underline-offset-4 transition-colors duration-150 ease-out hover:underline disabled:text-ink-subtle disabled:no-underline" data-id="element-1447" data-mp="1oebuzzvf57cr">
          Clear all
        </button>
      </div>

      <div className="mt-4" data-id="element-1448" data-mp="27dzigfdej7dp">
        <p className="text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-1449" data-mp="27d7m32bzd5md">
          Selected filters
        </p>
        {activeChips.length === 0 ? <p className="mt-2 text-body-sm text-ink-muted" data-id="element-1450" data-mp="2h3srb8m5t8e9">
            Nothing applied — you are seeing every published institution.
          </p> : <ul className="mt-2 flex flex-wrap gap-2" data-id="element-1451" data-mp="2l6ywse1g6vjo">
            {activeChips.map(chip => <li key={chip.key} data-id="element-1452" data-mp="2q50cqq3kueqx">
                <button type="button" onClick={() => clearFilter(chip.key)} className="inline-flex min-h-touch items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 pl-3 pr-2 text-body-sm font-medium text-brand-800 transition-colors duration-150 ease-out hover:border-brand-500" data-id="element-1453" data-mp="q27kfkn2xltf">
                  {chip.label}
                  <XIcon aria-hidden className="h-4 w-4" data-id="element-1454" data-mp="19q6wcytdqz0f" />
                  <span className="sr-only" data-id="element-1455" data-mp="19onfb5zwgjxl">Remove this filter</span>
                </button>
              </li>)}
          </ul>}
      </div>

      <div className="mt-5 space-y-4" data-id="element-1456" data-mp="1iretveo2nvtg">
        <Field label="Country" htmlFor="rb-country" data-id="element-1457" data-mp="1iqd6qbts7ivd">
          <Select id="rb-country" value={filters.country} onChange={event => setFilter('country', event.target.value)} data-id="element-1458" data-mp="1ds1y24f83eeq">
            {countries.map(item => <option key={item} value={item} data-id="element-1459" data-mp="3fjouilhvw1il">
                {item === 'All' ? 'All countries' : item}
              </option>)}
          </Select>
        </Field>

        <Field label="Location" htmlFor="rb-location" data-id="element-1460" data-mp="2007lcbcgvkcb">
          <Select id="rb-location" value={filters.location} onChange={event => setFilter('location', event.target.value)} data-id="element-1461" data-mp="1zzpiuwz7o9vy">
            {locations.map(item => <option key={item} value={item} data-id="element-1462" data-mp="1q8l0roe8o8ww">
                {item === 'All' ? 'Select location' : item}
              </option>)}
          </Select>
        </Field>

        <Field label="Institution type" htmlFor="rb-type" data-id="element-1463" data-mp="1haivgwnh2u3t">
          <Select id="rb-type" value={filters.institutionType} onChange={event => setFilter('institutionType', event.target.value)} data-id="element-1464" data-mp="13a86y09mxrlt">
            {['All', 'Federal', 'State', 'Private', 'International'].map(item => <option key={item} value={item} data-id="element-1465" data-mp="2xlvbwfm8r8fx">
                {item === 'All' ? 'All types' : item}
              </option>)}
          </Select>
        </Field>

        <Field label="Field of study" htmlFor="rb-field" data-id="element-1466" data-mp="2cyewloe4uqll">
          <Select id="rb-field" value={filters.field} onChange={event => setFilter('field', event.target.value)} data-id="element-1467" data-mp="27zaol12y2ywu">
            {fields.map(item => <option key={item} value={item} data-id="element-1468" data-mp="1y260uwtgavdx">
                {item === 'All' ? 'All fields' : item}
              </option>)}
          </Select>
        </Field>

        <Field label="Programme level" htmlFor="rb-level" data-id="element-1469" data-mp="1u1vrda6g9mub">
          <Select id="rb-level" value={filters.level} onChange={event => setFilter('level', event.target.value)} data-id="element-1470" data-mp="1p7rlyvcu1mvc">
            {['All', "Bachelor's", "Master's", 'PhD', 'Diploma'].map(item => <option key={item} value={item} data-id="element-1471" data-mp="1ac251bmi7zxh">
                {item === 'All' ? 'All levels' : item}
              </option>)}
          </Select>
        </Field>

        <div data-id="element-1472" data-mp="16cl0qlitd8tw">
          <label htmlFor="rb-tuition" className="block text-body-sm font-medium text-ink" data-id="element-1473" data-mp="16cujpp5fyfq3">
            Tuition range
          </label>
          <input id="rb-tuition" type="range" min={100000} max={TUITION_CEILING} step={50000} value={filters.tuitionMax} onChange={event => setFilter('tuitionMax', Number(event.target.value))} className="mt-3 h-2 w-full cursor-pointer appearance-none rounded-full bg-line accent-brand-700" data-id="element-1474" data-mp="3lxudo5uxgqpq" />
          <div className="cz-num mt-1.5 flex items-center justify-between text-caption text-ink-muted" data-id="element-1475" data-mp="33977fk10lvak">
            <span data-id="element-1476" data-mp="386pjvke8qsnb">₦0</span>
            <span className="font-semibold text-ink" data-id="element-1477" data-mp="3cyqsmwc7a18l">
              {filters.tuitionMax >= TUITION_CEILING ? '₦5M+' : `Up to ₦${filters.tuitionMax.toLocaleString()}`}
            </span>
          </div>
          <p className="mt-1.5 text-caption text-ink-subtle" data-id="element-1478" data-mp="2kho8ppe0x3ec">
            Applies to programmes priced in naira. Programmes quoted in another currency are not
            filtered out, because Campuzon does not hold an exchange rate to compare them with.
          </p>
        </div>

        <div className="border-t border-line pt-4" data-id="element-1479" data-mp="2m832cc7n9qbp">
          <Toggle checked={filters.scholarshipsOnly} onChange={value => setFilter('scholarshipsOnly', value)} label="With scholarships only" description="Only institutions and programmes with funding listed on Campuzon." data-id="element-1480" data-mp="2m6rvu4mte4gw" />
        </div>

        <div className="border-t border-line pt-4" data-id="element-1481" data-mp="1pj3qkn7uq6er">
          <Toggle checked={filters.internationalOnly} onChange={value => setFilter('internationalOnly', value)} label="International students welcome" description="Only institutions that have published an international intake." data-id="element-1482" data-mp="23hleom1o5kx7" />
        </div>

        <Field label="Application deadline" htmlFor="rb-deadline" data-id="element-1483" data-mp="39xxdmpcbf6tu">
          <Select id="rb-deadline" value={filters.deadline} onChange={event => setFilter('deadline', event.target.value as DeadlineWindow)} data-id="element-1484" data-mp="vpcxcmi3mgg0">
            {DEADLINES.map(item => <option key={item} value={item} data-id="element-1485" data-mp="mpgh999rypwk">
                {item}
              </option>)}
          </Select>
        </Field>

        <button type="button" onClick={() => setFilter('deadline', 'Anytime')} className="flex min-h-touch w-full items-center justify-center gap-2 rounded-lg border border-line text-body-sm font-medium text-ink transition-colors duration-150 ease-out hover:border-line-strong" data-id="element-1486" data-mp="ro1h37l477kh">
          <SlidersHorizontalIcon aria-hidden className="h-4 w-4" data-id="element-1487" data-mp="15lq009vmg4mw" />
          Reset deadline window
        </button>
      </div>
    </aside>;
}